/**
 * O tratamento do paciente e o catálogo de tratamentos (#811, clínica).
 *
 * Só nas organizações com a funcionalidade "clinica" ligada; sem ela, as rotas
 * respondem 404. O nome e o valor de tabela vêm do SISAC; o valor negociado se
 * ajusta, e cada ajuste guarda quem, quando e o motivo. Ligado a uma
 * oportunidade, o valor negociado é o valor dela no funil.
 */

import { z } from 'zod';
import { MudancaDeCamposSchema, ValoresDeCamposSchema } from './campos-personalizados.js';

const DataSchema = z.string().regex(/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/);

/** Micros na moeda: `4_800_000_000` = R$ 4.800,00. O mesmo formato do valor da oportunidade. */
const ValorSchema = z.object({
  quantia_micros: z.number().int().nonnegative(),
  moeda: z.string().length(3),
});

export const SituacaoDoTratamentoSchema = z.enum(['ativo', 'concluido', 'interrompido']);
export type SituacaoDoTratamentoDaApi = z.infer<typeof SituacaoDoTratamentoSchema>;

export const AjusteDeValorSchema = z.object({
  valor_anterior: ValorSchema,
  valor_novo: ValorSchema,
  motivo: z.string().nullable(),
  ajustado_em: z.string().datetime({ offset: true }),
  ajustado_por: z.object({ id: z.string().uuid(), nome: z.string().nullable() }).nullable(),
});
export type AjusteDeValor = z.infer<typeof AjusteDeValorSchema>;

export const TratamentoDoPacienteSchema = z.object({
  id: z.string().uuid(),
  /** O tratamento do catálogo de onde veio. */
  tratamento_id: z.string().uuid(),
  /** Copiados do SISAC no cadastro: a tabela pode mudar depois. */
  nome: z.string(),
  codigo: z.string(),
  valor_tabela: ValorSchema,
  valor_negociado: ValorSchema,
  /** A oportunidade do funil (id canônico) cujo valor é o negociado, ou `null`. */
  oportunidade_id: z.string().uuid().nullable(),
  /** `AAAA-MM-DD`: quando o paciente fechou. */
  fechado_em: DataSchema.nullable(),
  /** `AAAA-MM-DD`: quando o tratamento começa. */
  inicio_em: DataSchema.nullable(),
  medico: z.object({ id: z.string().uuid(), nome: z.string() }).nullable(),
  forma_pagamento: z.string().nullable(),
  parcelas: z.number().int().nullable(),
  sessoes_previstas: z.number().int().nullable(),
  situacao: SituacaoDoTratamentoSchema,
  criado_em: z.string().datetime({ offset: true }),
  versao: z.number().int().min(1),
  /** Do mais antigo ao mais novo. */
  ajustes: z.array(AjusteDeValorSchema),
  /** Os campos personalizados do tratamento (#568), pelo id do campo. Dado de saúde. */
  campos: ValoresDeCamposSchema,
});
export type TratamentoDoPaciente = z.infer<typeof TratamentoDoPacienteSchema>;

export const TratamentosDoPacienteSchema = z.object({
  /** O mais novo primeiro. */
  itens: z.array(TratamentoDoPacienteSchema),
});
export type TratamentosDoPaciente = z.infer<typeof TratamentosDoPacienteSchema>;

const CamposDoTratamento = {
  fechado_em: DataSchema.nullable(),
  inicio_em: DataSchema.nullable(),
  medico_id: z.string().uuid().nullable(),
  forma_pagamento: z.string().max(120).nullable(),
  parcelas: z.number().int().nullable(),
  sessoes_previstas: z.number().int().nullable(),
  /** Só os campos personalizados que mudaram (#568); `null` esvazia. */
  campos: MudancaDeCamposSchema.optional(),
};

export const CriarTratamentoDoPacienteSchema = z
  .object({
    tratamento_id: z.string().uuid(),
    /** A oportunidade (id canônico) deste paciente cujo valor passa a ser o negociado. */
    oportunidade_id: z.string().uuid().nullable(),
    /** Em micros, na moeda do tratamento. `null` usa o valor de tabela. */
    valor_negociado_micros: z.number().int().nonnegative().nullable(),
    /** Por que o valor nasce diferente do de tabela. */
    motivo: z.string().max(500).nullable(),
    ...CamposDoTratamento,
  })
  .strict();
export type CriarTratamentoDoPaciente = z.infer<typeof CriarTratamentoDoPacienteSchema>;

export const EditarTratamentoDoPacienteSchema = z
  .object({
    ...CamposDoTratamento,
    situacao: SituacaoDoTratamentoSchema,
    versao: z.number().int().min(1),
  })
  .strict();
export type EditarTratamentoDoPaciente = z.infer<typeof EditarTratamentoDoPacienteSchema>;

export const AjustarValorDoTratamentoSchema = z
  .object({
    /** Em micros, na moeda do tratamento. */
    valor_negociado_micros: z.number().int().nonnegative(),
    motivo: z.string().max(500).nullable(),
    versao: z.number().int().min(1),
  })
  .strict();
export type AjustarValorDoTratamento = z.infer<typeof AjustarValorDoTratamentoSchema>;

export const TratamentoDoCatalogoSchema = z.object({
  id: z.string().uuid(),
  codigo: z.string(),
  nome: z.string(),
  valor_tabela: ValorSchema,
  ativo: z.boolean(),
});
export type TratamentoDoCatalogo = z.infer<typeof TratamentoDoCatalogoSchema>;

export const CatalogoDeTratamentosSchema = z.object({
  /** Ativos e inativos, por nome. */
  itens: z.array(TratamentoDoCatalogoSchema),
});
export type CatalogoDeTratamentos = z.infer<typeof CatalogoDeTratamentosSchema>;
